import { ChangeEvent, useMemo } from "react";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import Form from "react-bootstrap/Form";
import classNames from "classnames";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { ClickableIcon } from "../clickable-icon/clickable-icon";
import { IdwFormLabelWithIconContainer } from "../idw-form-label-with-icon-container/idw-form-label-with-icon-container";
import styles from "./textarea-form-group.less";

type TextareaFormGroupProps = {
    controlId: string;
    labelWidth?: LabelWidthTypes;
    className?: string;
    automationId?: string;
    ariaLabel?: string;
    placeholder?: string;
    label: string;
    value: string;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    layout?: LayoutTypes;
    disabled: boolean;
    isRequired: boolean;
    afterLabelTooltipText?: string;
    afterInputElement?: React.ReactNode;
    afterLabelIcon?: IconDefinition;
    afterLabelIconDisabled?: boolean;
    afterLabelIconOnClick?: () => void;
    afterInputIcon?: IconDefinition;
    afterInputIconDisabled?: boolean;
    afterInputIconOnClick?: () => void;
    rightAlignedLabelElement?: React.ReactNode;
};

export const TextareaFormGroup = (props: TextareaFormGroupProps) => {
    const {
        controlId,
        labelWidth,
        className,
        automationId,
        ariaLabel,
        placeholder,
        label,
        value,
        onChange,
        layout,
        disabled,
        isRequired,
        afterInputElement,
        afterLabelTooltipText,
        afterLabelIcon,
        afterLabelIconDisabled,
        afterLabelIconOnClick,
        afterInputIcon,
        afterInputIconDisabled,
        afterInputIconOnClick,
        rightAlignedLabelElement
    } = props;

    const formGroupClassName = useMemo(() => {
        return layout === LayoutTypes.COLUMN ? styles.textareaFormGroupColumn : styles.textareaFormGroupRow;
    }, [layout]);

    return (
        <Form.Group controlId={controlId} className={classNames(formGroupClassName, className)}>
            <IdwFormLabelWithIconContainer
                controlId={controlId}
                labelWidth={labelWidth}
                layout={layout}
                label={label}
                isRequired={isRequired}
                afterLabelTooltipText={afterLabelTooltipText}
                afterLabelIcon={afterLabelIcon}
                afterLabelIconDisabled={afterLabelIconDisabled}
                afterLabelIconOnClick={afterLabelIconOnClick}
                rightAlignedLabelElement={rightAlignedLabelElement}
            />
            <Form.Control
                as="textarea"
                required={isRequired}
                automation-id={automationId}
                aria-label={ariaLabel}
                placeholder={placeholder}
                onChange={onChange}
                value={value}
                disabled={disabled}
            />
            {afterInputIcon && afterInputIconOnClick && (
                <div>
                    <ClickableIcon controlId={controlId} disabled={afterInputIconDisabled} icon={afterInputIcon} onClick={afterInputIconOnClick} className={styles.afterInputIcon} />
                </div>
            )}
            {afterInputElement ? afterInputElement : null}
        </Form.Group>
    );
};